import { useNavigate } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback } from 'react';

import List from '@mui/material/List';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import DialogContent from '@mui/material/DialogContent';
import ListItemButton from '@mui/material/ListItemButton';
import InputAdornment from '@mui/material/InputAdornment';

import { useLayoutNav } from 'src/hooks/use-layout-nav';
import { usePermissionChecker } from 'src/hooks/use-permission-checker';

import { navData as dashboardNavData } from 'src/layouts/config-nav-dashboard';

import { Iconify } from 'src/components/iconify';

import { useAuthContext } from 'src/auth/hooks';

import { flattenNavToCommands } from './flatten-nav-commands';

// ----------------------------------------------------------------------

function commandVisible(item, hasPermission, features) {
  // Same rule as the sidebar nav: a feature-flagged entry needs its feature on
  if (item.features?.some((feature) => !features?.[feature])) {
    return false;
  }
  if (!item.roles?.length) {
    return true;
  }
  return hasPermission(item.roles);
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const navData = useLayoutNav(dashboardNavData);

  const { user } = useAuthContext();
  const { hasPermission } = usePermissionChecker({ userRoles: user?.roles ?? [] });

  const commands = useMemo(() => {
    const flat = flattenNavToCommands(navData);
    return flat.filter((c) => commandVisible(c, hasPermission, user?.features));
  }, [navData, hasPermission, user?.features]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return commands;
    }
    return commands.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        (c.sub && c.sub.toLowerCase().includes(q))
    );
  }, [commands, query]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const handleSelect = useCallback(
    (path) => {
      navigate(path);
      handleClose();
    },
    [navigate, handleClose]
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!user) {
    return null;
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: { sx: { mt: 8 } },
      }}
    >
      <DialogContent sx={{ pb: 1, pt: 2 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search pages…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
          Navigation
        </Typography>
        <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
          {filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No matches
            </Typography>
          ) : (
            filtered.map((c) => (
              <ListItemButton key={`${c.path}-${c.title}`} onClick={() => handleSelect(c.path)}>
                <ListItemText
                  primary={c.title}
                  secondary={c.sub}
                  slotProps={{
                    secondary: {
                      typography: {
                        variant: 'caption',
                      },
                    },
                  }}
                />
              </ListItemButton>
            ))
          )}
        </List>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
          Press Esc to close · Ctrl K / ⌘ K
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
